import { pickOfflineAssignedUserIds } from "./eventOfflineAssignment";
import { pickOnGroundAssignedUserIds } from "./eventOnGroundAssignment";
import {
  buildTableBookingEventPayload,
  indexAreaPricingRows,
  normalizeAreaPricingRow,
  pickEventTableBookingFromItem,
  pricingOverrideKey,
  removeAreaPricingRow,
  rowHasOverride,
  upsertAreaPricingRow,
} from "./eventTableBooking";
import { buildTableBookingPriceHikePayload, pickTableBookingPriceHike } from "./tableBookingPriceHike";
import {
  buildTableBookingAutoPriceHikePayload,
  pickTableBookingAutoPriceHike,
} from "./tableBookingAutoPriceHike";
import { prepareTicketPhasesForApi } from "./ticketSalePhases";
import { pickTableBookingFixedFee } from "./tableFeeSettings";

function mapRelationIds(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item == null) return "";
      if (typeof item === "string" || typeof item === "number") {
        return String(item);
      }
      return String(item._id ?? item.id ?? "").trim();
    })
    .filter(Boolean);
}

function normalizeLatLong(event) {
  const raw = event?.latlong;
  if (Array.isArray(raw)) return raw;
  const coords = raw?.coordinates;
  if (Array.isArray(coords)) return coords;
  if (coords && typeof coords === "object") {
    return Object.values(coords);
  }
  return undefined;
}

function prepareTicketForApi(ticket) {
  if (!ticket || typeof ticket !== "object") return ticket;
  const { ticketstatus, ...rest } = ticket;
  let result = { ...rest };
  if (result._id === "") {
    const { _id, ...withoutId } = result;
    result = withoutId;
  }
  if (result.deliverycost === "") {
    const { deliverycost, ...withoutDelivery } = result;
    result = withoutDelivery;
  }
  return result;
}

/**
 * Build a full PUT /event/update payload from a list/detail event record.
 * Used for manager-only sold-out / table availability toggles on the view screen.
 */
export function buildEventUpdateFromRecord(event, patchFn) {
  if (!event?._id) return null;

  const booking = pickEventTableBookingFromItem(event);
  const organizerId =
    event.organizerid ??
    event.organizer?.[0]?._id ??
    event.organizer?.[0]?.id ??
    null;
  const tableBookingFixedFee = pickTableBookingFixedFee(event);

  let payload = {
    id: event._id,
    title: event.title,
    image: event.image ?? "",
    layoutimage: event.layoutimage ?? "",
    startdatetime: event.startdatetime,
    enddatetime: event.enddatetime,
    bookingstart: event.bookingstart,
    bookingend: event.bookingend,
    language:
      mapRelationIds(event.languageList).length > 0
        ? mapRelationIds(event.languageList)
        : event.language,
    genreid:
      mapRelationIds(event.genre).length > 0
        ? mapRelationIds(event.genre)
        : event.genreid,
    categoryid:
      mapRelationIds(event.category).length > 0
        ? mapRelationIds(event.category)
        : event.categoryid,
    organizerid: organizerId,
    artists:
      mapRelationIds(event.artistsDetails).length > 0
        ? mapRelationIds(event.artistsDetails)
        : event.artists,
    venuetobeanounced: Boolean(event.venuetobeanounced),
    aboutevent: event.aboutevent ?? "",
    terms: event.terms ?? "",
    tickets: (event.tickets || []).map((tk) =>
      prepareTicketPhasesForApi(prepareTicketForApi(tk))
    ),
    sponsored: Boolean(event.sponsored),
    is18Plus: Boolean(event.is18Plus ?? event.is18plus),
    layoutdesc: event.layoutdesc ?? "",
    bookingpercentage: event.bookingpercentage ?? 0,
    ...(tableBookingFixedFee != null ? { tableBookingFixedFee } : {}),
    clubid: event.clubid ?? event.clubId ?? null,
    offlineAssignedUserIds: pickOfflineAssignedUserIds(event),
    onGroundAssignedUserIds: pickOnGroundAssignedUserIds(event),
    location: event.location,
    latlong: normalizeLatLong(event),
    ...buildTableBookingEventPayload({
      tableBookingEnabled: booking.tableBookingEnabled,
      tableBookingLayoutId: booking.tableBookingLayoutId,
      tableBookingViewkey: booking.tableBookingViewkey,
      tableBookingSlotKey: booking.tableBookingSlotKey,
      tableBookingAreaPricing: booking.tableBookingAreaPricing,
    }),
    ...buildTableBookingPriceHikePayload(pickTableBookingPriceHike(event)),
    ...buildTableBookingAutoPriceHikePayload(pickTableBookingAutoPriceHike(event)),
  };

  if (typeof patchFn === "function") {
    payload = patchFn(payload);
  }

  return payload;
}

export function patchTicketSoldOutFlags(event, ticketId, flags) {
  return buildEventUpdateFromRecord(event, (base) => ({
    ...base,
    tickets: (base.tickets || []).map((ticket) => {
      const id = ticket?._id ?? ticket?.id;
      if (String(id) !== String(ticketId)) return ticket;
      return {
        ...ticket,
        ...(flags.soldout !== undefined ? { soldout: Boolean(flags.soldout) } : {}),
        ...(flags.offlineSoldOut !== undefined
          ? { offlineSoldOut: Boolean(flags.offlineSoldOut) }
          : {}),
      };
    }),
  }));
}

export function patchTableBookingAvailability(
  event,
  { areaId, tableId, manualAvailability, layoutPrice, currency = "INR" }
) {
  const booking = pickEventTableBookingFromItem(event);
  const rows = [...(booking.tableBookingAreaPricing || [])];
  const key = pricingOverrideKey(areaId, tableId);
  const existing = indexAreaPricingRows(rows).get(key);

  let nextRows = removeAreaPricingRow(rows, areaId, tableId);
  const merged = normalizeAreaPricingRow({
    areaId,
    ...(tableId ? { tableId } : {}),
    priceFromPerPerson:
      existing?.priceFromPerPerson ??
      existing?.price_from_per_person ??
      layoutPrice ??
      0,
    currency: existing?.currency ?? currency,
    priceDisplayLabel: existing?.priceDisplayLabel ?? "",
    minPartySize: existing?.minPartySize ?? "",
    maxPartySize: existing?.maxPartySize ?? "",
    description: existing?.description ?? "",
    manualAvailability,
    onlineSoldOut: existing?.onlineSoldOut,
    offlineSoldOut: existing?.offlineSoldOut,
    layoutPrice: layoutPrice ?? existing?.layoutPrice ?? 0,
  });

  if (rowHasOverride(merged)) {
    nextRows = upsertAreaPricingRow(nextRows, merged);
  }

  return buildEventUpdateFromRecord(event, (base) => ({
    ...base,
    ...buildTableBookingEventPayload({
      tableBookingEnabled: true,
      tableBookingLayoutId: booking.tableBookingLayoutId,
      tableBookingViewkey: booking.tableBookingViewkey,
      tableBookingSlotKey: booking.tableBookingSlotKey,
      tableBookingAreaPricing: nextRows,
    }),
  }));
}

export function patchTableBookingChannelSoldOut(
  event,
  {
    areaId,
    tableId,
    onlineSoldOut,
    offlineSoldOut,
    layoutPrice,
    currency = "INR",
    nestedTables = [],
  }
) {
  const booking = pickEventTableBookingFromItem(event);
  const rows = [...(booking.tableBookingAreaPricing || [])];

  const applyOne = (targetAreaId, targetTableId, flags) => {
    const key = pricingOverrideKey(targetAreaId, targetTableId);
    const existing = indexAreaPricingRows(rows).get(key);
    const merged = normalizeAreaPricingRow({
      areaId: targetAreaId,
      ...(targetTableId ? { tableId: targetTableId } : {}),
      priceFromPerPerson:
        existing?.priceFromPerPerson ??
        existing?.price_from_per_person ??
        layoutPrice ??
        0,
      currency: existing?.currency ?? currency,
      priceDisplayLabel: existing?.priceDisplayLabel ?? "",
      minPartySize: existing?.minPartySize ?? "",
      maxPartySize: existing?.maxPartySize ?? "",
      description: existing?.description ?? "",
      manualAvailability: existing?.manualAvailability ?? "AUTO",
      onlineSoldOut:
        flags.onlineSoldOut !== undefined
          ? flags.onlineSoldOut
          : existing?.onlineSoldOut,
      offlineSoldOut:
        flags.offlineSoldOut !== undefined
          ? flags.offlineSoldOut
          : existing?.offlineSoldOut,
      layoutPrice: layoutPrice ?? existing?.layoutPrice ?? 0,
    });
    let next = removeAreaPricingRow(rows, targetAreaId, targetTableId);
    if (rowHasOverride(merged)) {
      next = upsertAreaPricingRow(next, merged);
    }
    rows.splice(0, rows.length, ...next);
  };

  const flags = {
    ...(onlineSoldOut !== undefined
      ? { onlineSoldOut: Boolean(onlineSoldOut) }
      : {}),
    ...(offlineSoldOut !== undefined
      ? { offlineSoldOut: Boolean(offlineSoldOut) }
      : {}),
  };
  applyOne(areaId, tableId, flags);
  if (!tableId) {
    for (const nested of nestedTables || []) {
      const nestedTableId = nested?.tableId;
      if (!nestedTableId) continue;
      applyOne(areaId, nestedTableId, flags);
    }
  }

  return buildEventUpdateFromRecord(event, (base) => ({
    ...base,
    ...buildTableBookingEventPayload({
      tableBookingEnabled: true,
      tableBookingLayoutId: booking.tableBookingLayoutId,
      tableBookingViewkey: booking.tableBookingViewkey,
      tableBookingSlotKey: booking.tableBookingSlotKey,
      tableBookingAreaPricing: rows,
    }),
  }));
}
